import React, { useEffect, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import CustomInput from "./Input";
import Button from "./Button";
import { useDropzone } from "react-dropzone";
import Alert from "./Alert";
import html2canvas from "html2canvas";
import { showErrorToast } from "../utils/toast";
import { isWhitespaceString } from "../utils/regex";

const SignatureModal = ({
  open,
  onClose,
  onSubmit,
  signatureType,
  defaultName,
  isLoading,
  operatorSign = false,
  captureOnly = false,
}) => {
  const [fullName, setFullName] = useState(defaultName);
  const [initials, setInitials] = useState("");
  const [activeTab, setActiveTab] = useState(
    operatorSign ? "write" : "chooseStyle"
  );
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isCanvasEmpty, setIsCanvasEmpty] = useState(true);
  const signatureRef = React.useRef(null);
  const textRef = React.useRef(null);
  const nameLength = fullName?.length;
  const yCoord = nameLength < 10 ? 25 : 0;
  const xCoord = nameLength < 10 ? 4 : 0;

  useEffect(() => {
    setFullName(defaultName);
  }, [defaultName]);

  const dataURLtoFile = (dataurl, filename) => {
    var arr = dataurl.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[arr.length - 1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleAccept = () => {
    if (
      activeTab === "chooseStyle" &&
      (!fullName || isWhitespaceString(fullName))
    ) {
      setError("Full Name and Initials are required.");
      return;
    }
    if (
      activeTab === "write" &&
      (!signatureRef.current || signatureRef.current.isEmpty())
    ) {
      setError("Signature cannot be empty.");
      return;
    }
    if (activeTab === "upload" && !file) {
      setError("Please upload a signature file.");
      return;
    }

    if (activeTab === "write") {
      const signatureImage = signatureRef.current.toDataURL("image/png");
      let file = dataURLtoFile(signatureImage, "input");
      setGeneratedImage(signatureImage);
      setError(null);
      onSubmit(file, signatureType);
    } else if (activeTab === "chooseStyle") {
      generateImage();
    } else if (activeTab === "upload") {
      onSubmit(file, signatureType);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    // The CP review-first pages (captureOnly) scale a large photo to Sign's limits, then check 3 MB themselves.
    maxSize: (captureOnly ? 15 : 3) * 1024 * 1024,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        const typeRejections = fileRejections.filter((file) =>
          file.errors.some((error) => error.code === "file-invalid-type")
        );
        if (typeRejections.length > 0) {
          setError("Only JPG and PNG formats are allowed.");
        }

        const sizeRejections = fileRejections.filter((file) =>
          file.errors.some((error) => error.code === "file-too-large")
        );
        if (sizeRejections.length > 0) {
          setError(captureOnly ? "File size must be 15MB or less." : "File size must be 3MB or less.");
        }
      } else {
        const uploadedFile = URL.createObjectURL(acceptedFiles[0]);
        setError(null);
        setFile(acceptedFiles[0]);
      }
    },
  });

  const generateImage = async () => {
    if (!fullName) {
      setError("Full Name is required to generate the signature image.");
      return;
    }

    try {
      const canvas = await html2canvas(textRef.current, {
        useCORS: true,
        scale: nameLength > 15 ? 1 : 2,
        width: textRef.current.offsetWidth,
        height:
          nameLength > 15
            ? textRef.current.offsetHeight
            : nameLength < 10
            ? 20
            : textRef.current.offsetHeight * -1,
        backgroundColor: null,
        x: xCoord,
        y: yCoord,
        logging: false,
        letterRendering: 1,
        borderWidth: 0,
      });
      const dataUrl = canvas.toDataURL("image/png");
      let file = dataURLtoFile(dataUrl, "input");
      setGeneratedImage(file);
      setError(null);
      onSubmit(file, signatureType);
    } catch (err) {
      console.error("Error generating image:", err);
      setError("Failed to generate the signature image.");
    }
  };

  const signatureStyles = {
    style1: {
      fontFamily: "'Brush Script MT', cursive",
      fontSize: "20px",
      fontWeight: "normal",
      letterSpacing: "2px",
      textTransform: "capitalize",
      fontStyle: "italic",
    },
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    setIsCanvasEmpty(true);
  };

  const onBegin = () => {
    setIsCanvasEmpty(false);
  };

  const onEnd = () => {
    if (signatureRef.current?.isEmpty()) {
      setIsCanvasEmpty(true);
    }
  };

  return (
    <div>
      {open && (
        <div
          className="fixed inset-0 z-50 flex justify-center items-center bg-gray-800 bg-opacity-50"
          role="dialog"
        >
          <div className="relative bg-white rounded-lg shadow dark:bg-gray-700 w-full sm:w-3/5 lg:w-3/5 xl:w-3/5 max-w-full">
            <div className="flex m-4 items-center justify-between p-4 border-b rounded-t dark:border-gray-600">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Use your signature
              </h3>
              <button
                onClick={() => onClose()}
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
              >
                <svg
                  className="w-3 h-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>

            {!operatorSign && (
              <div className="p-4 space-y-4">
                {error && <Alert message={error} type={"error"} />}
                <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
                  Confirm your name, initials, and signature.
                </p>
                <div className="flex gap-4">
                  <div className="w-full sm:w-1/2 ">
                    <CustomInput
                      label="Full Name"
                      mandatory={true}
                      value={fullName}
                      className="rounded-lg focus:outline-none focus:border-blue-500"
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={isLoading} // Disable input field during loading
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="mt-1">
              <div className="flex justify-start gap-8 py-2 border-b border-gray-200">
                {!operatorSign && (
                  <button
                    onClick={() => handleTabChange("chooseStyle")}
                    className={`${
                      activeTab === "chooseStyle"
                        ? "font-semibold text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500"
                    } py-2 px-4`}
                  >
                    Text
                  </button>
                )}
                <button
                  onClick={() => handleTabChange("write")}
                  className={`${
                    activeTab === "write"
                      ? "font-semibold text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500"
                  } py-2 px-4`}
                >
                  Write
                </button>
                {!operatorSign && (
                  <button
                    onClick={() => handleTabChange("upload")}
                    className={`${
                      activeTab === "upload"
                        ? "font-semibold text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500"
                    } py-2 px-4`}
                  >
                    Upload
                  </button>
                )}
              </div>
            </div>

            {activeTab === "write" && (
              <div className="p-4 flex w-[80%] mx-auto sm:w-auto flex-col md:flex-row justify-center items-center sm:items-end gap-3">
                <div className="w-[300px] sm:w-[400px] h-[150px]">
                  <SignatureCanvas
                    ref={signatureRef}
                    penColor="black"
                    onBegin={onBegin}
                    onEnd={onEnd}
                    canvasProps={{
                      className:
                        "w-[300px] sm:w-[400px] h-[150px] border border-gray-300 rounded",
                    }}
                    disabled={isLoading} // Disable signature canvas during loading
                  />
                </div>
                <div className={isCanvasEmpty ? "invisible" : "visible"}>
                  <Button
                    labelColor="black"
                    label="Clear"
                    onClick={clearSignature}
                  />
                </div>
              </div>
            )}

            {activeTab === "chooseStyle" && (
              <div className="flex flex-col gap-4 max-h-20 overflow-y-auto divide-y divide-dashed">
                {Object.keys(signatureStyles).map((styleKey) => (
                  <div key={styleKey} className="flex items-center gap-4 p-4">
                    <label htmlFor={styleKey} className="flex-1 text-center">
                      <div style={signatureStyles[styleKey]} className="p-1">
                        <div className="flex flex-col items-center justify-between">
                          <div
                            ref={textRef}
                            className="flex-1 py-2 px-2 text-left overflow-hidden text-ellipsis whitespace-nowrap"
                          >
                            {fullName}
                          </div>

                          <div className="flex-1">{initials}</div>
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "upload" && (
              <div className="p-4">
                <div
                  {...getRootProps()}
                  className="border-2 border-dashed border-gray-400 p-8 text-center"
                >
                  <input {...getInputProps()} />
                  <h3 className="text-gray-500">Upload Your Signature</h3>
                </div>
                <p className="text-xs pt-1">
                  Accepted file formats: JPG, PNG. Maximum file size 3MB.
                </p>
                {file && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700">
                      File: {file.name}
                    </p>
                  </div>
                )}
                {error && (
                  <div className="mt-2 text-sm text-red-500">{error}</div>
                )}
              </div>
            )}

            <div className="flex flex-col p-4 border-t border-gray-200 rounded-b dark:border-gray-600">
              <p className="text-xs leading-tight">
                {captureOnly ? "Your signature is not submitted until you review the document and select Adopt and Sign." : <>
                By selecting Adopt and Sign, I agree that the signature and
                initials will be the electronic representation of my signature
                and initials for all purposes when I (or my agent) use them on
                documents, including legally binding contracts.
                </>}
              </p>
              <div className="flex mt-4">
                <Button
                  backgroundColor={"rgb(207 201 255)"}
                  labelColor={"black"}
                  className="h-10 border-0"
                  label={captureOnly ? "Use signature" : "Adopt and Sign"}
                  onClick={handleAccept}
                  isLoading={isLoading}
                  disabled={isLoading} // Disable button while loading
                />
                <Button
                  backgroundColor={"white"}
                  onClick={onClose}
                  labelColor={"black"}
                  className="h-10 border-0 border-white-600"
                  label={"Cancel"}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureModal;
